import { useQuery } from '@tanstack/react-query';
import type { EmployeeDetail } from '@workforce/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { t } from '@/lib/i18n';
import { country, employmentStatus, employmentType, flag, fmtDate, workArrangement } from '@/lib/format';
import { ChangePasswordForm } from '@/components/ChangePasswordForm';
import { Avatar, Badge, Card, DescList, PageHeader, Spinner, statusTone } from '@/components/ui';
import { PushCard } from '@/components/PushCard';

export function ProfilePage() {
  const { user } = useAuth();
  // Whole sentence with the e-mail in bold, in the right word order for each language.
  const [signedInBefore, signedInAfter] = t('Signed in as {email} (administrator account, not linked to an employee record).').split('{email}');
  const q = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api.get<EmployeeDetail>('/me/profile'), enabled: !!user?.employee });
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={t('My profile')} subtitle={t('Your details as held by the company. Ask an administrator to correct anything.')} />
      {user?.employee ? (
        q.isLoading ? (
          <Spinner />
        ) : (
          q.data && (
            <Card className="mb-6">
              <div className="mb-5 flex items-center gap-4">
                <Avatar name={q.data.fullName} size="lg" />
                <div>
                  <p className="text-lg font-semibold">{q.data.fullName}</p>
                  <p className="text-sm text-slate-500">
                    {q.data.position ?? ''} {q.data.departmentName ? `· ${q.data.departmentName}` : ''}
                  </p>
                  <Badge tone={statusTone(q.data.employmentStatus)}>{employmentStatus(q.data.employmentStatus)}</Badge>
                </div>
              </div>
              <DescList
                items={[
                  { label: t('Employee ID'), value: q.data.employeeCode },
                  { label: t('E-mail'), value: q.data.email },
                  { label: t('Phone'), value: q.data.phone ?? '—' },
                  { label: t('Location'), value: `${flag(q.data.countryCode)} ${q.data.city ? `${q.data.city}, ` : ''}${country(q.data.countryCode)}` },
                  { label: t('Time zone'), value: q.data.timezone ?? '—' },
                  { label: t('Manager'), value: q.data.managerName ?? '—' },
                  { label: t('Employment type'), value: employmentType(q.data.employmentType) },
                  { label: t('Work arrangement'), value: `${workArrangement(q.data.workArrangement)}${q.data.officeName ? ` · ${q.data.officeName}` : ''}` },
                  { label: t('Start date'), value: fmtDate(q.data.startDate) },
                  { label: t('Work schedule'), value: q.data.currentTerms?.workScheduleName ?? '—' },
                  { label: t('Holiday calendar'), value: q.data.currentTerms?.holidayCalendarName ?? '—' },
                ]}
              />
            </Card>
          )
        )
      ) : (
        <Card className="mb-6">
          <p className="text-sm text-slate-600">
            {signedInBefore}
            <strong>{user?.email}</strong>
            {signedInAfter}
          </p>
        </Card>
      )}
      <PushCard />
      <Card title={t('Change password')}>
        <div className="max-w-md">
          <ChangePasswordForm />
        </div>
      </Card>
    </div>
  );
}
